/**
 * Add Team Member Form Component
 * Task 5.4: Create Add Team Member Form
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserSearchSelect } from '../UserSearchSelect';
import { teamMemberService } from '@/lib/api/teamMember.service';
import { celebrationInvitationService } from '@/lib/api/celebration-invitation.service';
import { getRolePermissions } from '@/utils/permissions';
import type { UserContact } from '@/types/user-contact.types';
import type { TeamRole } from '@/types/teamMember.types';
import { ROLE_LABELS, ROLE_DESCRIPTIONS } from '@/types/teamMember.types';

type EditablePermissionKey =
  | 'canEditCelebration'
  | 'canManageTeam'
  | 'canManageEvents'
  | 'canManageGuests'
  | 'canAddRemoveGuests'
  | 'canManageVendors'
  | 'canManageServices'
  | 'canManageActivities'
  | 'canCreateTasks';

const ROLE_PRIORITY: TeamRole[] = ['organizer', 'event_manager', 'vendor_coordinator', 'host', 'member'];

const PERMISSION_OPTIONS: Array<{ key: EditablePermissionKey; labelKey: string }> = [
  { key: 'canEditCelebration', labelKey: 'common:team.addMember.permissions.editCelebrationDetails' },
  { key: 'canManageTeam', labelKey: 'common:team.addMember.permissions.manageTeamMembers' },
  { key: 'canManageEvents', labelKey: 'common:team.addMember.permissions.manageEvents' },
  { key: 'canManageGuests', labelKey: 'common:team.addMember.permissions.manageGuests' },
  { key: 'canAddRemoveGuests', labelKey: 'common:team.addMember.permissions.addOrRemoveGuests' },
  { key: 'canManageVendors', labelKey: 'common:team.addMember.permissions.manageVendors' },
  { key: 'canManageServices', labelKey: 'common:team.addMember.permissions.manageServices' },
  { key: 'canManageActivities', labelKey: 'common:team.addMember.permissions.manageActivities' },
  { key: 'canCreateTasks', labelKey: 'common:team.addMember.permissions.createTasks' },
];

const getClosestRoleForPermissionSelection = (
  currentRole: TeamRole,
  availableRoles: TeamRole[],
  key: EditablePermissionKey,
  checked: boolean
): TeamRole => {
  const desiredPermissions = {
    ...getRolePermissions(currentRole),
    [key]: checked,
  };

  const orderedRoles = ROLE_PRIORITY.filter((role) => availableRoles.includes(role));
  let bestRole = currentRole;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const candidateRole of orderedRoles) {
    const candidatePermissions = getRolePermissions(candidateRole);
    const mismatchCount = PERMISSION_OPTIONS.reduce((count, option) => {
      return count + (candidatePermissions[option.key] === desiredPermissions[option.key] ? 0 : 1);
    }, 0);

    if (
      mismatchCount < bestScore ||
      (mismatchCount === bestScore && candidateRole === currentRole)
    ) {
      bestRole = candidateRole;
      bestScore = mismatchCount;
    }
  }

  return bestRole;
};

interface AddTeamMemberFormProps {
  celebrationId: number;
  onSuccess: () => void;
  onCancel: () => void;
}

export const AddTeamMemberForm: React.FC<AddTeamMemberFormProps> = ({
  celebrationId,
  onSuccess,
  onCancel
}) => {
  const { t } = useTranslation(['common']);
  const [selectedUser, setSelectedUser] = useState<UserContact | null>(null);
  const [selectedRole, setSelectedRole] = useState<TeamRole>('member');
  const [addMode, setAddMode] = useState<'existing' | 'invite'>('existing');
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [celebrationQuotaInput, setCelebrationQuotaInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roles: TeamRole[] = ['organizer', 'event_manager', 'host', 'vendor_coordinator', 'member'];
  const selectedRolePermissions = getRolePermissions(selectedRole);

  const handlePermissionToggle = (key: EditablePermissionKey, checked: boolean) => {
    const nextRole = getClosestRoleForPermissionSelection(selectedRole, roles, key, checked);
    setSelectedRole(nextRole);
  };

  const parseCelebrationQuota = (): number | null => {
    if (selectedRole !== 'host') {
      return null;
    }

    const trimmed = celebrationQuotaInput.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < 0) {
      throw new Error(t('common:team.addMember.errors.invalidHostQuota'));
    }

    return parsed;
  };

  const handleSubmit = async (event?: React.SyntheticEvent) => {
    event?.preventDefault();
    
    if (addMode === 'existing' && !selectedUser) {
      setError(t('common:team.addMember.errors.selectUser'));
      return;
    }

    if (addMode === 'invite' && (!inviteName.trim() || !inviteEmail.trim())) {
      setError(t('common:team.addMember.errors.nameEmailRequired'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const celebrationGuestQuota = parseCelebrationQuota();

      if (addMode === 'invite') {
        await celebrationInvitationService.inviteTeamMember(celebrationId, {
          full_name: inviteName.trim(),
          email: inviteEmail.trim(),
          role: selectedRole,
        });
      } else {
        await teamMemberService.addTeamMember(celebrationId, {
          user_id: selectedUser!.user_id || selectedUser!.id,
          role: selectedRole,
          celebration_guest_quota: celebrationGuestQuota,
        });
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error adding team member:', err);
      setError(err.response?.data?.message || err.response?.data?.error || t('common:team.addMember.errors.addFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            {t('common:team.addMember.title')}
          </CardTitle>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label>{t('common:team.addMember.labels.addMethod')}</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={addMode === 'existing' ? 'default' : 'outline'}
                onClick={() => setAddMode('existing')}
              >
                {t('common:team.addMember.modes.existingContact')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={addMode === 'invite' ? 'default' : 'outline'}
                onClick={() => setAddMode('invite')}
              >
                {t('common:team.addMember.modes.inviteByEmail')}
              </Button>
            </div>
          </div>

          {addMode === 'existing' ? (
            <div className="space-y-2">
              <Label htmlFor="user">{t('common:team.addMember.labels.userRequired')}</Label>
              <UserSearchSelect
                onSelect={setSelectedUser}
                placeholder={t('common:team.addMember.placeholders.searchUser')}
              />
              {selectedUser && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
                  <span className="text-sm text-blue-900">
                    {selectedUser.full_name || `${selectedUser.contact_user?.first_name} ${selectedUser.contact_user?.last_name}`}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedUser(null)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="invite-name">{t('common:team.addMember.labels.fullNameRequired')}</Label>
                <Input
                  id="invite-name"
                  value={inviteName}
                  onChange={(event) => setInviteName(event.target.value)}
                  placeholder={t('common:team.addMember.placeholders.fullName')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-email">{t('common:team.addMember.labels.emailRequired')}</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder={t('common:team.addMember.placeholders.email')}
                />
                <p className="text-xs text-gray-500">
                  {t('common:team.addMember.hints.inviteEmail')}
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="role">{t('common:team.addMember.labels.roleRequired')}</Label>
            <select
              id="role"
              title={t('common:team.addMember.labels.selectRoleTitle')}
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as TeamRole)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            >
              {roles.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
            {selectedRole && (
              <p className="text-sm text-gray-600">
                {ROLE_DESCRIPTIONS[selectedRole]}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('common:team.addMember.labels.permissions')}</Label>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="space-y-2">
                {PERMISSION_OPTIONS.map((permission) => (
                  <label
                    key={permission.key}
                    className={`flex items-center gap-2 text-sm ${selectedRolePermissions[permission.key] ? 'text-slate-700' : 'text-slate-500'}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRolePermissions[permission.key]}
                      onChange={(event) => handlePermissionToggle(permission.key, event.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span>{t(permission.labelKey)}</span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {t('common:team.addMember.hints.permissions')}
              </p>
            </div>
          </div>

          {addMode === 'existing' && selectedRole === 'host' && (
            <div className="space-y-2">
              <Label htmlFor="celebration_quota">{t('common:team.addMember.labels.celebrationQuota')}</Label>
              <Input
                id="celebration_quota"
                type="number"
                min={0}
                step={1}
                value={celebrationQuotaInput}
                onChange={(event) => setCelebrationQuotaInput(event.target.value)}
                placeholder={t('common:team.addMember.placeholders.celebrationQuota')}
              />
              <p className="text-xs text-gray-500">
                {t('common:team.addMember.hints.celebrationQuota')}
              </p>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={submitting}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || (addMode === 'existing' ? !selectedUser : !inviteName.trim() || !inviteEmail.trim())}
            >
              {submitting
                ? t('common:status.saving')
                : addMode === 'invite'
                  ? t('common:team.addMember.actions.sendInvitation')
                  : t('common:team.addMember.actions.addTeamMember')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
